import React, { useEffect, useState } from 'react';
import {
  PageHeader,
  PageHeaderButton,
  PageHeaderDropdown,
  PageHeaderDropdownButton,
} from '@amzn/stencil-react-components/page';
import { Col, Spacer } from '@amzn/stencil-react-components/layout';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarColorScheme } from '@amzn/stencil-react-components/avatar';
import { IconGlobe, IconPerson, IconUndo, IconQuestionCircle, IconSize } from '@amzn/stencil-react-components/icons';
import { Text, Label, H3, H5 } from '@amzn/stencil-react-components/text';
import { useGlobalContext, useUserDataContext } from '../hooks';
import {
  FlyoutContent,
  Flyout,
} from '@amzn/stencil-react-components/flyout';
import { ToggleSwitch } from '@amzn/stencil-react-components/toggle-switch';
import { Button } from '@amzn/stencil-react-components/button';
import { Input } from '@amzn/stencil-react-components/form';
import { LANGUAGES, LANGUAGE_TO_LOCALE_MAP, LOCALE_TO_LANGUAGE_MAP } from '../constants/languages';
import { Select } from '@amzn/stencil-react-components/form';
import { useTranslation } from 'react-i18next';
import { isRequestHelpEnabledForWarehouse } from '../util/allow_lists';
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner';
import { useErrorHandler } from 'react-error-boundary';

export const Header = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {
    backendServiceClient,
    userToken,
    isLoggedIn,
    setUserData,
    userDetails,
    targetUser,
    setTargetUser,
    isAdminViewEnabled,
    setIsAdminViewEnabled,
    setUserDetails
  } = useGlobalContext();
  const { dataFailure, dataFailureError } = useUserDataContext();
  const handleError = useErrorHandler();

  const [newTargetUser, setNewTargetUser] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [hasTargetUserChangeFailed, setTargetUserChangeFailed] = useState(false);

  const [isUserUpdating, setIsUserUpdating] = useState(false);
  const [isUserFlyoutOpen, setUserFlyoutOpen] = useState(false);
  const [isLanguageFlyoutOpen, setLanguageFlyoutOpen] = useState(false);
  const [localIsAdminViewEnabled, setLocalIsAdminViewEnabled] = useState(isAdminViewEnabled);

  useEffect(() => {
    if (dataFailure) {
      handleError(dataFailureError);
    }
  }, [dataFailure, dataFailureError]);

  const generateHeaderItems = () => {
    const items = [
      <PageHeaderDropdownButton
        onClick={
          () => {
            setNewLanguage(LOCALE_TO_LANGUAGE_MAP.get(i18n.language)!);
            setLanguageFlyoutOpen(true);
          }
        }
        icon={<IconGlobe title={t('changeLanguage')} />}
      >
        {t('changeLanguage')}
      </PageHeaderDropdownButton>,
      userDetails.isAdmin &&
        <PageHeaderDropdownButton
          onClick={() => setUserFlyoutOpen(true)}
          icon={<IconPerson title={t('changeUser')} />}
        >
          {t('changeUser')}
        </PageHeaderDropdownButton>,
      <PageHeaderDropdownButton data-cy="logout-btn" icon={<IconUndo title={t('signOut')} />} onClick={() => {
        setUserData('', null);
      }}>
        {t('signOut')}
      </PageHeaderDropdownButton>
    ];

    return items.filter(e => e); // remove elements that evaluate to false
  };

  const updateTargetUser = async () => {
    setIsUserUpdating(true);
    try {
      const userData = await backendServiceClient.getEmployeeDetails(userToken, newTargetUser);
      setUserDetails({
        employeeId: userData.employeeId!,
        login: newTargetUser,
        emailAddress: userDetails.emailAddress, // We want to make sure when we test email we use our own
        warehouseId: userData?.warehouseId!,
        locale: userData?.locale || Intl.DateTimeFormat().resolvedOptions().timeZone,
        isAdmin: true,
        managerLogin: userData?.managerLogin || '',
      });
      setIsAdminViewEnabled(localIsAdminViewEnabled);
      setTargetUser({
        login: newTargetUser,
        employeeId: userData.employeeId!,
      });
      setTargetUserChangeFailed(false);
      setUserFlyoutOpen(false);
      setIsUserUpdating(false);
    } catch (error) {
      setTargetUserChangeFailed(true);
      setIsUserUpdating(false);
    }
  };

  const requestHelpEnabled = isRequestHelpEnabledForWarehouse(userDetails.warehouseId);
  const requestHelpTitle = requestHelpEnabled ? t('requestSupport') : t('faqs');

  return(
    <>
      <Flyout isOpen={isUserFlyoutOpen} close={() => {}}>
        <FlyoutContent
          titleText={t('changeUser')}
          onCloseButtonClick={() => setUserFlyoutOpen(false)}
          buttons={[
            <Button onClick={updateTargetUser} disabled={isUserUpdating}>
              {t('done')}
            </Button>
          ]}>
          <Text>{t('changeTargetUserMessage')}</Text>
          <Spacer height={16}/>
          <Col gridGap="S200">
            <Label htmlFor="targetUserInput">
              {t('targetUser')}
            </Label>
            <Input
              id="targetUserInput"
              type="text"
              onChange={(event: any) => setNewTargetUser(event.target.value.trim())}
              defaultValue={newTargetUser}
            />
            <Label htmlFor="view-as-admin">
              {t('changeUserAdminMessage')}
            </Label>
            <ToggleSwitch
              id="view-as-admin"
              onChange={() => setLocalIsAdminViewEnabled(!localIsAdminViewEnabled)}
              defaultChecked={localIsAdminViewEnabled}
            />
          </Col>
          {hasTargetUserChangeFailed &&
            <MessageBanner type={MessageBannerType.Error}>
              {t('targetUserChangeFailure')}
            </MessageBanner>
          }
        </FlyoutContent>
      </Flyout>
      <Flyout isOpen={isLanguageFlyoutOpen} close={() => {}}>
        <FlyoutContent
          titleText={t('changeLanguage')}
          onCloseButtonClick={() => setLanguageFlyoutOpen(false)}
          buttons={[
            <Button onClick={
              () => {
                i18n.changeLanguage(LANGUAGE_TO_LOCALE_MAP.get(newLanguage));
                setLanguageFlyoutOpen(false);
              }
            }>
              {t('done')}
            </Button>
        ]}>
          <Text>{t('languageSelectionMessage')}</Text>
          <Spacer height={16}/>
          <Col gridGap="S200">
            <Label htmlFor="language-selector" id="language-selector-label">
              <Text fontSize="T300" fontWeight="medium">
                {t('language')}
              </Text>
            </Label>
            <Select
              labelId="language-selector-label"
              id="language-selector"
              options={LANGUAGES}
              value={newLanguage}
              onChange={setNewLanguage}
            />
          </Col>
        </FlyoutContent>
      </Flyout>
      <PageHeader>
        <PageHeaderButton onClick={() => navigate('/')}>
          <H3 color="white">PAK</H3>
        </PageHeaderButton>
        <Spacer flex={1} />

        {isLoggedIn &&
          <>
            <PageHeaderButton
              data-cy="request-help-header"
              onClick={() => navigate('/request-support')}
              icon={<IconQuestionCircle size={IconSize.Medium}
              title={requestHelpTitle} />}
            >
              <Spacer width={4}/>
              <H5 color="white">{requestHelpTitle}</H5>
              <Spacer width={4}/>
            </PageHeaderButton>
            <PageHeaderDropdown
              data-cy="user-dropdown"
              items={generateHeaderItems()}
            >
              <Avatar username={targetUser.login} isUsernameClickable={false} colorScheme={AvatarColorScheme.Inverted} />
            </PageHeaderDropdown>
          </>
        }
      </PageHeader>
    </>
  );
}
